import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { User, FileText, Upload, CheckCircle, LogOut, Loader2, Trash2, CreditCard, ShoppingCart, Download, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateProfile, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [documents, setDocuments] = useState([]);
  const [certificationRequests, setCertificationRequests] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingData, setBillingData] = useState({ billing_name: '', billing_nif: '', billing_address: '', billing_email: '' });
  const [processingStatus, setProcessingStatus] = useState(null);
  const [finalizingDocs, setFinalizingDocs] = useState(false);
  const [professionalNotes, setProfessionalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Profile form
  const [profileData, setProfileData] = useState({
    full_name: '',
    phone: '',
    specialty: '',
    collegiate_number: '',
    official_college: '',
    country: '',
    areas_to_certify: ''
  });

  // Upload form
  const [uploadData, setUploadData] = useState({
    file: null,
    document_type: 'titulo_academico',
    description: ''
  });

  // Certification request form
  const [certData, setCertData] = useState({
    areas_to_certify: '',
    additional_notes: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        specialty: user.specialty || '',
        collegiate_number: user.collegiate_number || '',
        official_college: user.official_college || '',
        country: user.country || '',
        areas_to_certify: user.areas_to_certify?.join(', ') || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments();
      fetchProcessingStatus();
    } else if (activeTab === 'upload') {
      // Load existing professional notes
      axios.get(`${API}/documents/professional-notes`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (r.data.notes) setProfessionalNotes(r.data.notes); })
        .catch(() => {});
    } else if (activeTab === 'certifications') {
      fetchCertificationRequests();
    } else if (activeTab === 'services') {
      fetchOrders();
      if (user) {
        setBillingData(prev => ({
          ...prev,
          billing_name: prev.billing_name || user.full_name || '',
          billing_email: prev.billing_email || user.email || '',
        }));
      }
    }
  }, [activeTab]);

  const fetchDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const response = await axios.get(`${API}/documents/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchCertificationRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API}/certifications/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificationRequests(response.data);
    } catch (error) {
      console.error('Error fetching certification requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const areas = profileData.areas_to_certify.split(',').map(area => area.trim()).filter(area => area);
    const result = await updateProfile({
      ...profileData,
      areas_to_certify: areas
    });

    if (result.success) {
      toast({
        title: "Perfil actualizado",
        description: "Sus datos han sido guardados exitosamente.",
      });
    } else {
      toast({
        title: "Error",
        description: result.error,
        variant: "destructive"
      });
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast({
        title: "Error",
        description: "Por favor seleccione un archivo",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('document_type', uploadData.document_type);
    formData.append('description', uploadData.description);

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Documento subido",
        description: "El documento se ha subido exitosamente.",
      });

      setUploadData({ file: null, document_type: 'titulo_academico', description: '' });
      document.getElementById('file-upload').value = '';
      fetchDocuments();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al subir el documento",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este documento?')) {
      return;
    }

    try {
      await axios.delete(`${API}/documents/delete/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Documento eliminado",
        description: "El documento ha sido eliminado exitosamente.",
      });

      fetchDocuments();
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al eliminar el documento",
        variant: "destructive"
      });
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const res = await axios.get(`${API}/documents/download/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Error", description: "Error al descargar el documento", variant: "destructive" });
    }
  };

  const fetchProcessingStatus = async () => {
    try {
      const res = await axios.get(`${API}/documents/processing-status`, { headers: { Authorization: `Bearer ${token}` } });
      setProcessingStatus(res.data);
    } catch { /* ignore */ }
  };

  const handleFinalizeDocs = async () => {
    if (!window.confirm('¿Ha terminado de subir toda su documentacion? Se procedera al analisis automatico de su expediente.')) return;
    setFinalizingDocs(true);
    try {
      const res = await axios.post(`${API}/documents/finalize`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Procesando", description: res.data.message });
      setProcessingStatus({ status: 'processing' });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Error al procesar", variant: "destructive" });
    } finally {
      setFinalizingDocs(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.post(`${API}/documents/professional-notes`, { notes: professionalNotes }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Guardado", description: "Sus observaciones han sido guardadas correctamente." });
    } catch (err) {
      toast({ title: "Error", description: "Error al guardar las observaciones", variant: "destructive" });
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCertificationRequest = async (e) => {
    e.preventDefault();
    const areas = certData.areas_to_certify.split(',').map(area => area.trim()).filter(area => area);

    if (areas.length === 0) {
      toast({
        title: "Error",
        description: "Por favor indique las áreas a certificar",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(
        `${API}/certifications/request`,
        {
          areas_to_certify: areas,
          additional_notes: certData.additional_notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Solicitud enviada",
        description: "Su solicitud de certificación ha sido enviada exitosamente.",
      });

      setCertData({ areas_to_certify: '', additional_notes: '' });
      fetchCertificationRequests();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al enviar la solicitud",
        variant: "destructive"
      });
    }
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${API}/payments/my-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleCheckout = async (serviceType) => {
    if (!billingData.billing_name || !billingData.billing_email) {
      toast({ title: "Error", description: "Nombre y email de facturación son obligatorios.", variant: "destructive" });
      return;
    }
    setCheckoutLoading(serviceType);
    try {
      const response = await axios.post(`${API}/payments/checkout`,
        { service_type: serviceType, ...billingData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      }
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Error al procesar el pago", variant: "destructive" });
      setCheckoutLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_revision: 'bg-blue-100 text-blue-800',
      aprobada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800',
      certificada: 'bg-amber-100 text-amber-800'
    };

    const statusLabels = {
      pendiente: 'Pendiente',
      en_revision: 'En Revisión',
      aprobada: 'Aprobada',
      rechazada: 'Rechazada',
      certificada: 'Certificada'
    };

    return (
      <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
        {statusLabels[status] || status}
      </Badge>
    );
  };

  const getDocumentTypeLabel = (type) => {
    const types = {
      titulo_academico: 'Título Académico',
      certificado_formacion: 'Certificado de Formación',
      cv_profesional: 'CV Profesional',
      experiencia_laboral: 'Experiencia Laboral',
      foto_perfil: 'Foto de Perfil',
      otro: 'Otro'
    };
    return types[type] || type;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ background: '#F6F5F0' }}>
      {/* Header */}
      <header style={{ background: '#0B1D3A' }} className="shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#fff', letterSpacing: '2px' }}>CERTILEX<sup style={{ fontSize: '0.5em' }}>&reg;</sup></h1>
              <p className="text-sm" style={{ color: '#C5A55A' }}>Panel de Profesional</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="font-semibold" style={{ color: '#fff' }}>{user.full_name}</p>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{user.email}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                className="flex items-center"
                style={{ borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-3xl grid-cols-5 mb-8">
            <TabsTrigger value="profile">
              <User className="w-4 h-4 mr-2" />
              Perfil
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Servicios
            </TabsTrigger>
            <TabsTrigger value="documents">
              <FileText className="w-4 h-4 mr-2" />
              Documentos
            </TabsTrigger>
            <TabsTrigger value="upload">
              <Upload className="w-4 h-4 mr-2" />
              Subir
            </TabsTrigger>
            <TabsTrigger value="certifications">
              <CheckCircle className="w-4 h-4 mr-2" />
              Certificaciones
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Mi Perfil Profesional</CardTitle>
                <CardDescription>
                  Actualice sus datos profesionales
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Nombre Completo</Label>
                      <Input
                        id="full_name"
                        value={profileData.full_name}
                        onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Teléfono</Label>
                      <Input
                        id="phone"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="specialty">Especialidad</Label>
                      <Input
                        id="specialty"
                        value={profileData.specialty}
                        onChange={(e) => setProfileData({ ...profileData, specialty: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">País</Label>
                      <Input
                        id="country"
                        value={profileData.country}
                        onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="collegiate_number">Número de Colegiado</Label>
                      <Input
                        id="collegiate_number"
                        value={profileData.collegiate_number}
                        onChange={(e) => setProfileData({ ...profileData, collegiate_number: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="official_college">Colegio Oficial</Label>
                      <Input
                        id="official_college"
                        value={profileData.official_college}
                        onChange={(e) => setProfileData({ ...profileData, official_college: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="areas_to_certify">Áreas de Competencia a Certificar</Label>
                    <Input
                      id="areas_to_certify"
                      value={profileData.areas_to_certify}
                      onChange={(e) => setProfileData({ ...profileData, areas_to_certify: e.target.value })}
                      placeholder="Separe con comas"
                    />
                  </div>

                  <Button type="submit" style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }}>
                    Guardar Cambios
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {/* Service Card: Standard */}
              <Card style={{ border: '2px solid #E2DDD3' }}>
                <CardHeader className="text-center">
                  <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Certificacion Estandar</CardTitle>
                  <CardDescription>10 a 20 dias habiles</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#0B1D3A' }}>1.500 &euro;</div>
                  <ul className="text-left mt-4 space-y-2 text-sm" style={{ color: '#4A4A5A' }}>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Tramitacion completa</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Acta notarial + expediente colegial</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Alta en registro CERTILEX 12 meses</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Factura desgravable</li>
                  </ul>
                  <Button
                    className="w-full mt-6"
                    style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }}
                    onClick={() => handleCheckout('certificacion_registro')}
                    disabled={checkoutLoading !== null}
                    data-testid="checkout-standard"
                  >
                    {checkoutLoading === 'certificacion_registro' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Contratar
                  </Button>
                </CardContent>
              </Card>

              {/* Service Card: Urgent */}
              <Card style={{ border: '2px solid #c0392b' }}>
                <CardHeader className="text-center" style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#c0392b', color: '#fff', fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 1rem', borderRadius: '20px' }}>URGENTE</div>
                  <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A', marginTop: '0.5rem' }}>Certificacion Urgente</CardTitle>
                  <CardDescription>Resolucion en 7 dias</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#0B1D3A' }}>1.800 &euro;</div>
                  <ul className="text-left mt-4 space-y-2 text-sm" style={{ color: '#4A4A5A' }}>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Verificacion documental acelerada</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Turno preferente en notaria</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Acta notarial + expediente colegial</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C5A55A' }} />Ideal si quedan menos de 30 dias</li>
                  </ul>
                  <Button
                    className="w-full mt-6"
                    style={{ background: '#c0392b', color: '#fff', fontWeight: 700 }}
                    onClick={() => handleCheckout('certificacion_registro_urgente')}
                    disabled={checkoutLoading !== null}
                    data-testid="checkout-urgent"
                  >
                    {checkoutLoading === 'certificacion_registro_urgente' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Contratar Urgente
                  </Button>
                </CardContent>
              </Card>

              {/* Billing Data */}
              <Card style={{ border: '2px solid #E2DDD3' }}>
                <CardHeader>
                  <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A', fontSize: '1.1rem' }}>Datos de Facturacion</CardTitle>
                  <CardDescription>Para la emision de la factura</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="billing_name" className="text-xs">Nombre / Razon social *</Label>
                    <Input id="billing_name" value={billingData.billing_name} onChange={e => setBillingData({...billingData, billing_name: e.target.value})} placeholder="Nombre completo" data-testid="billing-name" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="billing_email" className="text-xs">Email de facturacion *</Label>
                    <Input id="billing_email" type="email" value={billingData.billing_email} onChange={e => setBillingData({...billingData, billing_email: e.target.value})} placeholder="email@ejemplo.com" data-testid="billing-email" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="billing_nif" className="text-xs">NIF / CIF</Label>
                    <Input id="billing_nif" value={billingData.billing_nif} onChange={e => setBillingData({...billingData, billing_nif: e.target.value})} placeholder="Opcional" data-testid="billing-nif" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="billing_address" className="text-xs">Direccion fiscal</Label>
                    <Input id="billing_address" value={billingData.billing_address} onChange={e => setBillingData({...billingData, billing_address: e.target.value})} placeholder="Opcional" data-testid="billing-address" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Orders History */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#0B1D3A' }}>Historial de Pedidos</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingOrders ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#C5A55A' }} /></div>
                ) : orders.length === 0 ? (
                  <p className="text-center py-6" style={{ color: '#4A4A5A' }}>No tiene pedidos todavia.</p>
                ) : (
                  <div className="space-y-3">
                    {orders.map((order, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#F6F5F0', border: '1px solid #E2DDD3' }}>
                        <div>
                          <p className="font-semibold text-sm" style={{ color: '#0B1D3A' }}>{order.service_name}</p>
                          <p className="text-xs" style={{ color: '#4A4A5A' }}>{order.invoice_number} &mdash; {order.created_at?.slice(0,10)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold" style={{ color: '#0B1D3A' }}>{order.amount} EUR</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${order.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {order.status === 'paid' ? 'Pagado' : 'Pendiente'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Mis Documentos</CardTitle>
                    <CardDescription>
                      Documentos subidos para su certificacion
                    </CardDescription>
                  </div>
                  {documents.length > 0 && processingStatus?.status !== 'completed' && (
                    <Button
                      onClick={handleFinalizeDocs}
                      disabled={finalizingDocs || processingStatus?.status === 'processing'}
                      style={{ background: '#0B1D3A', color: '#fff', fontWeight: 700 }}
                      data-testid="finalize-docs-btn"
                    >
                      {finalizingDocs || processingStatus?.status === 'processing' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> He terminado de subir</>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Processing status banner */}
                {processingStatus?.status === 'processing' && (
                  <div className="mb-4 p-4 rounded-lg flex items-center gap-3" style={{ background: '#FFF7ED', border: '1px solid #C5A55A' }}>
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#C5A55A' }} />
                    <div>
                      <p className="font-semibold text-sm" style={{ color: '#0B1D3A' }}>Analizando su expediente...</p>
                      <p className="text-xs" style={{ color: '#4A4A5A' }}>Recibira un email con el resumen y borrador notarial cuando finalice.</p>
                    </div>
                  </div>
                )}
                {processingStatus?.status === 'completed' && (
                  <div className="mb-4 p-4 rounded-lg" style={{ background: '#F0FDF4', border: '1px solid #16a34a' }}>
                    <p className="font-semibold text-sm" style={{ color: '#16a34a' }}>Expediente en proceso</p>
                    <p className="text-xs" style={{ color: '#4A4A5A' }}>Hemos recibido su documentacion. Nuestro equipo la esta revisando y le informara de los siguientes pasos. Le propondremos una cita por videoconferencia con el notario.</p>
                  </div>
                )}
                {loadingDocuments ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C5A55A' }} />
                  </div>
                ) : documents.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No ha subido documentos aún. Vaya a la pestaña "Subir" para agregar documentos.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{doc.filename}</h4>
                          <p className="text-sm text-gray-600">{getDocumentTypeLabel(doc.document_type)}</p>
                          {doc.description && (
                            <p className="text-sm text-gray-500 mt-1">{doc.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Subido: {new Date(doc.uploaded_at).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                            data-testid={`download-doc-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteDocument(doc.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload">
            <Card>
              <CardHeader>
                <CardTitle>Subir Documentos</CardTitle>
                <CardDescription>
                  Suba títulos académicos, certificados, CV y otros documentos relevantes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFileUpload} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="file-upload">Archivo (máx. 10MB)</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="document_type">Tipo de Documento</Label>
                    <select
                      id="document_type"
                      value={uploadData.document_type}
                      onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2" style={{ borderColor: '#E2DDD3', focusRingColor: '#C5A55A' }}
                    >
                      <option value="titulo_academico">Título Académico</option>
                      <option value="certificado_formacion">Certificado de Formación</option>
                      <option value="cv_profesional">CV Profesional</option>
                      <option value="experiencia_laboral">Experiencia Laboral</option>
                      <option value="foto_perfil">Foto de Perfil</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción (opcional)</Label>
                    <Textarea
                      id="description"
                      value={uploadData.description}
                      onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                      placeholder="Añada una breve descripción del documento"
                      rows={3}
                    />
                  </div>

                  <Button
                    type="submit"
                    style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Subir Documento
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Professional Notes */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Observaciones sobre su documentacion</CardTitle>
                <CardDescription>
                  Explique lo que considere necesario en relacion a la documentacion aportada: contexto, aclaraciones, experiencia no reflejada en los documentos, o cualquier informacion relevante.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Textarea
                    value={professionalNotes}
                    onChange={(e) => setProfessionalNotes(e.target.value)}
                    placeholder="Ejemplo: Mi experiencia en cirugia estetica se remonta a 2005, aunque solo he podido aportar certificados desde 2010. He realizado mas de 500 intervenciones en la Clinica X. El diploma del master fue emitido en ingles por la Universidad de..."
                    rows={6}
                    style={{ borderColor: '#E2DDD3' }}
                    data-testid="professional-notes"
                  />
                  <Button
                    onClick={handleSaveNotes}
                    disabled={savingNotes || !professionalNotes.trim()}
                    style={{ background: '#0B1D3A', color: '#fff', fontWeight: 600 }}
                    data-testid="save-notes-btn"
                  >
                    {savingNotes ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Guardar observaciones
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certifications Tab */}
          <TabsContent value="certifications">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Solicitar Certificación</CardTitle>
                  <CardDescription>
                    Solicite la certificación de sus competencias profesionales
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCertificationRequest} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cert_areas">Áreas de Competencia a Certificar</Label>
                      <Input
                        id="cert_areas"
                        value={certData.areas_to_certify}
                        onChange={(e) => setCertData({ ...certData, areas_to_certify: e.target.value })}
                        placeholder="Separe con comas: Cirugía Cardiovascular, Ecocardiografía..."
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notas Adicionales (opcional)</Label>
                      <Textarea
                        id="notes"
                        value={certData.additional_notes}
                        onChange={(e) => setCertData({ ...certData, additional_notes: e.target.value })}
                        placeholder="Añada información adicional relevante para su solicitud"
                        rows={4}
                      />
                    </div>

                    <Button type="submit" style={{ background: '#C5A55A', color: '#0B1D3A', fontWeight: 700 }}>
                      Enviar Solicitud
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Mis Solicitudes</CardTitle>
                  <CardDescription>
                    Estado de sus solicitudes de certificación
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingRequests ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    </div>
                  ) : certificationRequests.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      No tiene solicitudes de certificación aún.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {certificationRequests.map((req) => (
                        <div key={req.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                Áreas: {req.areas_to_certify.join(', ')}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                Solicitado: {new Date(req.submitted_at).toLocaleDateString('es-ES')}
                              </p>
                            </div>
                            {getStatusBadge(req.status)}
                          </div>
                          {req.additional_notes && (
                            <p className="text-sm text-gray-600 mt-2">
                              <strong>Notas:</strong> {req.additional_notes}
                            </p>
                          )}
                          {req.reviewer_notes && (
                            <div className="mt-3 p-3 bg-blue-50 rounded">
                              <p className="text-sm text-gray-700">
                                <strong>Comentarios del revisor:</strong> {req.reviewer_notes}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;
